<template>
  <search-main v-bind="$attrs">
    <template v-if="hasCustomHeading" #search-summary-heading>
      <slot name="search-summary-heading">
        <h1 data-test="search-summary-hierarchy" v-text="customHeading" />
      </slot>
    </template>

    <template #before-summary>
      <slot name="before-summary" />
    </template>

    <template #search-guides>
      <slot name="search-guides" />
    </template>

    <template #search-pagepart>
      <slot name="search-pagepart" />
    </template>

    <template #popular-locations>
      <slot name="popular-locations" />
    </template>

    <template #lower-pagepart>
      <slot name="lower-pagepart" />
    </template>

    <template #pagination-controls="{ paginationData, searchFilters }">
      <slot
        name="pagination-controls"
        :pagination-data="paginationData"
        :search-filters="searchFilters"
      />
    </template>
  </search-main>
</template>

<script setup>
import { computed, useSlots } from 'vue'
import { isSearchUrlRoute } from '../isSearchUrlComponent'

const searchStore = useSearchStoreSetup(usePinia())
const route = useRoute()
const { $t } = useNuxtApp()
const slots = useSlots()

if (isSearchUrlRoute(route)) {
  onServerPrefetch(async () => {
    await searchStore.s()
  })
}

// Custom H1 for lat/lng searches (with optional facet)
const customHeading = computed(() => {
  const hasLatLng = route.query.lat && route.query.lng
  const placename = route.query.placename
  const facetSlug = route.query.facet
  const results = searchStore.context.results
  const categoryName = results?.category?.name

  // Only override if we have lat/lng coordinates
  if (!hasLatLng) {
    return null
  }

  // Look up facet display name if facet parameter exists
  let facetName = null
  if (facetSlug && typeof facetSlug === 'string' && results?.facetModels) {
    const facet = results.facetModels[facetSlug]
    facetName = facet?.name || null
  }

  // Check if this is a user location search (not a named place)
  const isUserLocationSearch =
    results?.within && results?.point && !results?.hierarchyName

  // Case 1: "Near me" search (user's current location)
  if (!placename && isUserLocationSearch) {
    // With facet: "{facet} {category} near me" or "{facet} campsites near me"
    if (facetName) {
      if (categoryName) {
        return $t('{facetName} {categoryName} near me', {
          facetName,
          categoryName,
        })
      }
      return $t('{facetName} campsites near me', { facetName })
    }

    // Without facet: Use existing "near me" translations
    if (categoryName) {
      return $t(`${categoryName} near me`)
    }
    return $t('Campsites near me')
  }

  // Case 2: Named location search (lat/lng with placename)
  if (!placename) {
    return null
  }

  let decodedPlacename
  try {
    decodedPlacename = decodeURIComponent(String(placename).replace(/\+/g, ' '))
  } catch (_e) {
    // Malformed URI - fall back to API heading
    console.error('Malformed placename in URL query:', placename, _e)
    return null
  }

  // With facet: "{facet} {category} near {placename}" or "{facet} campsites near {placename}"
  if (facetName) {
    if (categoryName) {
      return $t('{facetName} {categoryName} near {placename}', {
        facetName,
        categoryName,
        placename: decodedPlacename,
      })
    }
    return $t('{facetName} campsites near {placename}', {
      facetName,
      placename: decodedPlacename,
    })
  }

  // Without facet: Use existing translations
  if (categoryName) {
    return $t('{categoryName} near {placename}', {
      categoryName,
      placename: decodedPlacename,
    })
  }

  return $t('Campsites near {placename}', { placename: decodedPlacename })
})

// Only provide the slot to SearchMain if parent provided content OR we have a custom heading
// This allows SearchMain's fallback H1 (from backend API) to render when neither condition is true

const hasCustomHeading = computed(() => {
  return Boolean(slots['search-summary-heading'] || customHeading.value)
})
</script>
